import { Fragment, useCallback, useMemo, useState } from 'react';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import FiltersConsulateList from 'system/geographic/components/consulate/list';
import { HttpResponse, HttpResponseList } from 'shell/models/http-response';
import { useEffect } from 'react';
import { Consulate } from 'shell/models/consulate';
import { FiltersFormConsulate, ConsulateClient } from './types';
import { useFormik } from 'formik';
import { transformDataset } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { initialValuesConsulate } from '..';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { generatePath, useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { CommonConsulateCrumbs } from '../constants';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import ListHeader from 'shell/components/shared/list-header';
import { headCells, validationSchemaConsulateFilter, authsObj } from './constants';
import { Typography } from '@mui/material';

const ConsulateListPage = (): React.ReactElement => {
  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<ConsulateClient>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<ConsulateClient>>({
    element: null,
    open: false,
  });

  const filterTable = useFilterTable<FiltersFormConsulate>();
  const {
    data,
    get: getConsulateList,
    // abort,
    error,
  } = useHttpFetch<HttpResponseList<Consulate>>(`${__MUNICIPALITY_API_URL__}/geo/consolato`, {
    data: {
      payload: {
        list: [],
      },
    },
  });

  const { delete: deleteConsulate } = useHttpFetch<HttpResponse<Consulate>>(
    `${__MUNICIPALITY_API_URL__}/geo/consolato`
  );

  const consulateList: Array<ConsulateClient> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((consulate) => ({
      code: consulate.key?.codice || '',
      description: consulate.nome || '',
      municipality: consulate.comune?.nome || '',
    }));
  }, [data]);

  const onSubmit = useCallback(
    (formValues: FiltersFormConsulate) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterConsulateForm.setFieldValue('page', 0);
      getConsulateList(queryString);
    },
    [getConsulateList]
  );

  const filterConsulateForm = useFormik({
    initialValues: initialValuesConsulate,
    validationSchema: validationSchemaConsulateFilter,
    onSubmit,
  });
  useEffect(() => {
    filterConsulateForm.setValues(filterTable.values || initialValuesConsulate);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterConsulateForm.values);
  }, [filterConsulateForm.values]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload) {
      return 0;
    }
    return data.payload.list.length;
  }, [error, data?.payload]);

  const navigate = useNavigate();

  const auths = useActionAuthorization(authsObj);

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterConsulateForm.values, orders };
      const queryString = transformDataset(newDataset);
      return getConsulateList(queryString).then(() => {
        filterConsulateForm.setFieldValue('orders', orders);
      });
    },
    [filterConsulateForm.values, getConsulateList, filterConsulateForm.setFieldValue]
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<ConsulateClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/geografiche/estero/consolato/dettaglio/:id', {
            id: cell.code,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<ConsulateClient> => {
      return new Promise((resolve) => {
        navigate(
          generatePath('/sistema/geografiche/estero/consolato/modifica/:id', {
            id: cell.code,
          })
        );
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<ConsulateClient> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<ConsulateClient>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (page) => {
      filterConsulateForm.setFieldValue('page', page || 0);
    },
    [filterConsulateForm.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterConsulateForm.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      filterConsulateForm.setValues(newDataset);
    },
    [filterConsulateForm.setFieldValue, filterConsulateForm.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={CommonConsulateCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo consolato"
        addBtnLabel="Aggiungi nuovo consolato"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/geografiche/estero/consolato/nuovo');
        }}
        title="Gestione Consolati"
      />
      <div className="pmi-my-3">
        <FiltersConsulateList
          filterConsulateForm={filterConsulateForm}
          // onChangeMunicipality={debounceChange}
        />
      </div>
      {data?.payload ? (
        !(consulateList.length > 0) ? (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        ) : (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterConsulateForm.values.orders}
            activeActions={auths}
            page={filterConsulateForm.values.page}
            rowsPerPage={filterConsulateForm.values.rowsPerPage}
            body={consulateList}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Consolati"
          />
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Consolato"
        message={`Confermare la cancellazione del consolato ${
          deleteAlertModal.element?.description || ''
        }?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteConsulate(`/${deleteAlertModal.element?.code}`).then(() => {
            setDeleteAlertModal({ open: false, element: null });
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
          });
        }}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Consolato"
        message={`La cancellazione del consolato ${
          deleteSuccessModal.element?.description || ''
        } è avvenuta con successo`}
        onClose={() => {
          const queryString = transformDataset(filterConsulateForm.values);
          getConsulateList(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
        onOk={() => {
          const queryString = transformDataset(filterConsulateForm.values);
          getConsulateList(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
      />
    </Fragment>
  );
};

ConsulateListPage.displayName = 'Consulate List page component';

export default ConsulateListPage;
